import { useEffect, useRef, useState, type JSX } from 'react'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import CalculateIcon from '@mui/icons-material/Calculate'
import { applyNumberBlur, formatNumberDisplay, resolveNumberDigits } from './formatNumber'
import { NumberCalculator } from './NumberCalculator'

interface FormattedNumberFieldProps {
  value: number | null
  onChange: (v: number | null) => void
  precision?: number
  scale?: number
  disabled?: boolean
  required?: boolean
  readOnly?: boolean
  size?: 'small' | 'medium'
  fullWidth?: boolean
  variant?: 'outlined' | 'standard' | 'filled'
  placeholder?: string
  startAdornment?: React.ReactNode
  /** Extra end adornment rendered before the calculator button */
  endAdornment?: React.ReactNode
  autoFocus?: boolean
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  /**
   * When the calculator affordance is shown.
   * - always (default) — header fields
   * - focus — tabular cells (idle cells stay clean)
   * - never — suppress entirely
   */
  calculatorVisibility?: 'always' | 'focus' | 'never'
}

export function FormattedNumberField(props: FormattedNumberFieldProps): JSX.Element {
  const {
    value,
    onChange,
    disabled,
    required,
    readOnly,
    size,
    fullWidth,
    variant,
    placeholder,
    startAdornment,
    endAdornment,
    autoFocus,
    inputProps,
    calculatorVisibility = 'always',
  } = props
  const { precision, scale } = resolveNumberDigits(props as unknown as Record<string, unknown>)

  const [focused, setFocused] = useState(false)
  const [text, setText] = useState<string>(() => formatNumberDisplay(value, precision, scale))
  const [calculatorOpen, setCalculatorOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const committedOnEnterRef = useRef(false)
  // Always mount the button when not 'never'. For 'focus' (tabular), TabularGrid
  // CSS fades `.tabular-cell-adornment` until cell hover/focus-within — do NOT
  // unmount on blur, or mousedown→blur races the click and the popover never opens.
  const showCalculator = calculatorVisibility !== 'never'

  useEffect(() => {
    if (!focused) {
      setText(formatNumberDisplay(value, precision, scale))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused])

  function handleFocus(): void {
    setFocused(true)
  }

  function commitDraft(): void {
    const next = applyNumberBlur(text, value, precision, scale)
    onChange(next)
    setText(formatNumberDisplay(next, precision, scale))
  }

  function handleBlur(): void {
    setFocused(false)
    if (committedOnEnterRef.current) {
      committedOnEnterRef.current = false
      return
    }
    commitDraft()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing || calculatorOpen) return
    commitDraft()
    if (e.metaKey || e.ctrlKey || e.altKey) return
    e.preventDefault()
    committedOnEnterRef.current = true
    setFocused(false)
    ;(e.target as HTMLInputElement).blur()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setText(e.target.value)
  }

  function handleCalculatorApply(v: number): void {
    onChange(v)
    setText(formatNumberDisplay(v, precision, scale))
  }

  function handleCalculatorMouseDown(e: React.MouseEvent): void {
    // Keep input focus — otherwise blur unmounts/hides the gesture target
    // before click, and the popover never opens (tabular focus-gated chrome).
    e.preventDefault()
    if (disabled || readOnly) return
    setCalculatorOpen(true)
  }

  function handleCalculatorClick(): void {
    // Keyboard activation (Enter/Space on the button).
    if (disabled || readOnly) return
    setCalculatorOpen(true)
  }

  return (
    <>
      <TextField
        value={text}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        size={size}
        fullWidth={fullWidth}
        variant={variant}
        placeholder={placeholder}
        autoFocus={autoFocus}
        inputProps={{ inputMode: 'decimal', readOnly, ...inputProps }}
        InputProps={{
          startAdornment: startAdornment ? (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ) : undefined,
          endAdornment:
            endAdornment || showCalculator ? (
              <InputAdornment
                position="end"
                className={calculatorVisibility === 'focus' ? 'tabular-cell-adornment' : undefined}
              >
                {endAdornment}
                {showCalculator ? (
                  <IconButton
                    ref={anchorRef}
                    type="button"
                    size="small"
                    aria-label="Open calculator"
                    disabled={disabled || readOnly}
                    onMouseDown={handleCalculatorMouseDown}
                    onClick={handleCalculatorClick}
                  >
                    <CalculateIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ) : undefined,
        }}
      />
      {showCalculator ? (
        <NumberCalculator
          open={calculatorOpen}
          anchorEl={anchorRef.current}
          onClose={() => setCalculatorOpen(false)}
          onApply={handleCalculatorApply}
          initialValue={value}
          precision={precision}
          scale={scale}
        />
      ) : null}
    </>
  )
}
